import { useState } from "react";

import { PersonIcon } from "@vericus/cadmus-icons";
import {
  Avatar,
  Button,
  Combobox,
  Command,
  LinkButton,
  SelectButton,
  Text,
  vars,
} from "@vericus/cadmus-ui";

import { IssueStatus } from "@/generated/graphql";

import { StatusCell } from "../issue";
import {
  buttonContent,
  comboboxActions,
  comboboxContent,
  commandList,
  commandRoot,
  counter,
} from "./filter-combobox.css";

/**
 * Searchable Multi Select Item
 */
export interface SearchMultiSelectItem {
  value: string;
  label: string;
  description?: string;
}

interface SearchMultiSelectProps {
  title: string;
  items: SearchMultiSelectItem[];
  placeholder?: string;
  selectedItemIds: string[]; // controlled
  onSelectedChange: (selected: string[]) => void;
  onApply: () => void;
  onClear: () => void;
  isSearchable: boolean;
  withIcon: boolean;
  withAvatar: boolean;
}

export function FilterCombobox({
  title,
  items,
  placeholder = "Search...",
  selectedItemIds,
  onSelectedChange,
  onApply,
  onClear,
  isSearchable,
  withIcon = false,
  withAvatar = false,
}: SearchMultiSelectProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleItemSelection = (value: string) => {
    if (selectedItemIds.includes(value)) {
      onSelectedChange(selectedItemIds.filter((id) => id !== value));
    } else {
      onSelectedChange([...selectedItemIds, value]);
    }
  };

  const handleSelectAll = () => {
    onSelectedChange(items.map((item) => item.value));
  };

  const handleClearAll = () => {
    onSelectedChange([]);
    onClear();
    setOpen(false);
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  return (
    <Combobox.Root open={open} onOpenChange={setOpen}>
      <Combobox.Trigger asChild>
        <SelectButton
          icon={
            withIcon ? (
              <PersonIcon
                label="assignee"
                size={24}
                color={vars.colors.primary}
              />
            ) : null
          }
        >
          <div className={buttonContent}>
            <Text kind="bodyLine" as="div">
              {title}
            </Text>
            {selectedItemIds.length > 0 && (
              <Text kind="bodyLine" as="div" className={counter}>
                {selectedItemIds.length}
              </Text>
            )}
          </div>
        </SelectButton>
      </Combobox.Trigger>

      <Combobox.Content className={comboboxContent}>
        <Command.Root className={commandRoot}>
          {isSearchable && (
            <Command.Input
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={placeholder}
            />
          )}

          <div className={comboboxActions}>
            <LinkButton color="lilac500" onClick={handleSelectAll}>
              Select All
            </LinkButton>
            <LinkButton color="lilac500" onClick={handleClearAll}>
              Clear All
            </LinkButton>
          </div>

          <Command.List className={commandList}>
            {filteredItems.map((item) => (
              <Command.Item
                key={item.value}
                value={item.label}
                checkable
                checkBoxColor="primary"
                onSelect={() => toggleItemSelection(item.value)}
                data-selected={
                  selectedItemIds.includes(item.value) ? "true" : undefined
                }
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {withAvatar && (
                    <Avatar
                      name={item.label}
                      size="md"
                      shade={items.findIndex((i) => i.value === item.value)}
                    />
                  )}
                  <div
                    style={{
                      maxWidth: 200,
                      textOverflow: "ellipsis",
                      wordWrap: "break-word",
                    }}
                  >
                    {title === "Status" ? (
                      <StatusCell status={item.value as IssueStatus} />
                    ) : (
                      item.label
                    )}
                  </div>
                </div>
              </Command.Item>
            ))}
          </Command.List>
        </Command.Root>

        <Button kind="primary" onClick={handleApply} style={{ margin: 10 }}>
          Apply
        </Button>
      </Combobox.Content>
    </Combobox.Root>
  );
}
